import type {
  SuggestionsResponse,
  ConversationsResponse,
  Conversation,
  ConversationDetail,
  APIMessage,
  ChatMessage,
  DeleteConversationResponse,
  ContentBlock,
} from '../types';
import { nebulaFetchJson } from './nebulaClient';

const API_BASE = '/api/v1';

export async function fetchSuggestions(): Promise<SuggestionsResponse> {
  return nebulaFetchJson(`${API_BASE}/suggestions`);
}

export async function checkHealth(): Promise<{ status: string }> {
  return nebulaFetchJson(`${API_BASE}/health`);
}

export async function fetchConversations(): Promise<ConversationsResponse> {
  return nebulaFetchJson(`${API_BASE}/conversations`);
}

export async function fetchConversation(id: string): Promise<ConversationDetail> {
  return nebulaFetchJson(`${API_BASE}/conversations/${id}`);
}

export async function deleteConversation(
  id: string
): Promise<DeleteConversationResponse> {
  return nebulaFetchJson(`${API_BASE}/conversations/${id}`, {
    method: 'DELETE',
  });
}

// Transform API messages to internal ChatMessage format
export function transformAPIMessagesToChatMessages(apiMessages: APIMessage[]): ChatMessage[] {
  // Debug: Log API response to see what we're receiving
  console.log('[Nebula] Loading conversation messages:', {
    messageCount: apiMessages.length,
    messages: apiMessages.map((m, i) => ({
      index: i,
      role: m.role,
      contentLength: m.content?.length || 0,
      hasVisualization: !!m.metadata?.visualization,
      visualizationType: m.metadata?.visualization?.config?.type,
      metadata: m.metadata,
    })),
  });

  return apiMessages.map((msg, index) => {
    // Use pre-built blocks from API if available (saved conversations),
    // otherwise build contentBlocks from individual fields (fallback)
    let contentBlocks: ContentBlock[] | undefined;

    if (msg.blocks && msg.blocks.length > 0) {
      // Saved conversations: Use pre-built blocks directly
      contentBlocks = msg.blocks;
    } else {
      // Fallback: Build contentBlocks from individual fields
      const blocks: ContentBlock[] = [];

      if (msg.content && msg.content.trim()) {
        blocks.push({ type: 'text', content: msg.content });
      }

      if (msg.metadata.visualization) {
        blocks.push({ type: 'visualization', data: msg.metadata.visualization });
      }

      contentBlocks = blocks.length > 0 ? blocks : undefined;
    }

    const transformedMessage = {
      id: `msg_${Date.now()}_${index}`,
      role: msg.role,
      content: msg.content,
      timestamp: new Date(msg.timestamp),
      contentBlocks,
      // Assistant messages might have visualizations, thinking, etc.
      ...(msg.role === 'assistant' && {
        thinkingContent: undefined, // Historical messages don't preserve thinking
        citations: undefined, // Historical messages don't preserve citations
        toolUses: undefined, // Historical messages don't preserve tool uses
      }),
    };

    console.log(`[Nebula] Transformed message ${index}:`, {
      role: transformedMessage.role,
      hasBlocks: !!msg.blocks,
      contentBlocksCount: transformedMessage.contentBlocks?.length || 0,
      contentBlocks: transformedMessage.contentBlocks,
    });

    return transformedMessage;
  });
}
